/** Patterns for common server-side template syntaxes that must be stripped before sending */
const TEMPLATE_INJECTION_PATTERNS = [/\{\{.*?\}\}/g, /\{%.*?%\}/g, /\$\{.*?\}/g, /<\?.*?\?>/g, /\[\[.*?\]\]/g];
const DANGEROUS_TAG_BLOCK_REGEX = /<\s*(script|style|iframe|object|embed|noscript)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi;
// Matches any dangerous tag — opening, closing, self-closing, or even an unterminated
// `<script` fragment (the `>` is optional) — so no `<script`/`<style`/... substring can
// survive to an HTML sink. `[^<>]` + a single `\s*` keep it linear-time (ReDoS-safe).
const DANGEROUS_TAG_REGEX = /<\s*\/?(?:script|style|iframe|object|embed|noscript)[^<>]*>?/gi;
// Excludes `<` from the class so a run of `<<<<` can't backtrack polynomially (ReDoS).
// `replaceToFixedPoint`/`stripHtmlTags` loop this, so nested tags are still fully removed.
const ANY_TAG_REGEX = /<[^<>]+>/g;
const BR_TAG_REGEX = /<br\s*\/?>/gi;
const BLOCK_CLOSE_TAG_REGEX = /<\/(?:p|div|li|tr|h[1-6])>/gi;
const ANCHOR_TAG_REGEX = /<a\b[^>]*?\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>([\s\S]*?)<\/a>/gi;
const MULTIPLE_NEWLINES_REGEX = /\n{3,}/g;
const HTML_ENTITY_REGEX = /&(nbsp|lt|gt|quot|#39|amp|rarr|bull|middot|ndash);/gi;
// Zero-width (U+200B-U+200D, U+2060, U+FEFF) and bidi-control (U+202A-U+202E,
// U+2066-U+2069) characters — can hide or visually reorder injected text inside
// a prompt so it reads differently to the model than a byte-level pattern check
// would see. Written as \u escapes, not literal glyphs, since they're invisible.
const ZERO_WIDTH_AND_BIDI_REGEX = /[\u200B-\u200D\u2060\uFEFF\u202A-\u202E\u2066-\u2069]/g;
// Double quotes and backticks are the characters a `"""..."""` prompt fence is
// made of, so untrusted text never gets to keep any: each becomes an apostrophe.
// Collapsing only runs of 3+ is not enough — a value ending in exactly two (`""`)
// survives the collapse and, placed against the closing fence, forms a five-quote
// run the fence cannot survive. The typographic forms fold too — curly singles
// and doubles (U+2018-U+201F), double and triple primes and their reversed
// forms (U+2033, U+2034, U+2036, U+2037), the modifier-letter double prime
// (U+02BA), the Hebrew gershayim (U+05F4), the ditto mark (U+3003), CJK corner
// quotes (U+301D-U+301F), the heavy ornamental pair (U+276E, U+276F) and the
// full-width pair (U+FF02, U+FF07) and the full-width grave (U+FF40, which
// NFKC maps to a backtick) — since a `"""` built from those still
// reads like a closed fence to a model. Written as \u escapes so the class
// stays greppable. ASCII apostrophes are left alone (O'Brien, don't).
const PROMPT_FENCE_QUOTE_REGEX =
  /["`\u02BA\u05F4\u2018-\u201F\u2033\u2034\u2036\u2037\u276E\u276F\u3003\u301D-\u301F\uFF02\uFF07\uFF40]/g;
// After the conversion above only apostrophes remain; any run of 3+ is collapsed
// to one so the converted text can't form a `'''` fence either.
const PROMPT_FENCE_RUN_REGEX = /'{3,}/g;
// Any run of whitespace, including the Unicode spaces and line separators `\s`
// covers, collapses to one ASCII space in the echo-check normalization.
const WHITESPACE_RUN_REGEX = /\s+/g;
const HTML_ENTITY_MAP: Record<string, string> = {
  nbsp: " ",
  lt: "<",
  gt: ">",
  quot: '"',
  "#39": "'",
  amp: "&",
  rarr: "\u2192",
  bull: "\u2022",
  middot: "\u00b7",
  ndash: "\u2013",
};
const TAG_REGEX = /<\/?([a-zA-Z0-9]+)([^>]*)>/g;
const ATTRIBUTE_REGEX = /([a-zA-Z0-9:-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "a",
  "ul",
  "ol",
  "li",
  "span",
  "img",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "td",
  "th",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "hr",
  "sup",
  "sub",
]);
const ALLOWED_ATTRIBUTES = new Set([
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "width",
  "height",
  "class",
  "style",
  "cellpadding",
  "cellspacing",
  "border",
  "bgcolor",
  "align",
  "valign",
  "colspan",
  "rowspan",
]);
const ALLOWED_TARGETS = new Set(["_blank", "_self", "_parent", "_top"]);
const CLASS_NAME_REGEX = /^[a-zA-Z0-9_\- ]+$/;
const DIMENSION_REGEX = /^\d+%?$/;
const CSS_COMMENT_REGEX = /\/\*[\s\S]*?\*\//g;
const DANGEROUS_STYLE_REGEX = /expression\s*\(|url\s*\(|javascript\s*:|@import|behavior\s*:|binding\s*:/i;
const SAFE_URL_REGEX = /^(https?:|mailto:|\/|#|cid:)/i;
const SAFE_IMG_DATA_URI_REGEX = /^data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=]+$/i;

/**
 * Escapes HTML entities in attribute values.
 * @param value - Raw attribute value.
 * @returns Escaped value safe for attribute output.
 */
export function escapeAttributeValue(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Applies a global regex replacement repeatedly until the string stops changing.
 * A single pass can be defeated by nested input — stripping the inner match from
 * `<scr<script>ipt>` re-forms `<script>` — so looping to a fixed point closes that
 * bypass (CWE-20/116, "incomplete multi-character sanitization").
 * @param input - String to sanitize.
 * @param pattern - Global (`g`-flagged) regex whose matches are replaced.
 * @param replacement - Replacement string (defaults to "").
 * @returns Input with the replacement applied to a fixed point.
 */
function replaceToFixedPoint(input: string, pattern: RegExp, replacement = ""): string {
  let current = input;
  let previous: string;
  do {
    previous = current;
    current = current.replace(pattern, replacement);
  } while (current !== previous);
  return current;
}

/**
 * Strips every HTML tag from a string, looping until no tag remains so nested
 * `<scr<script>ipt>`-style input cannot smuggle a tag past a single pass.
 * @param input - Raw HTML or text.
 * @returns Input with all `<...>` tags removed.
 */
export function stripHtmlTags(input: string): string {
  return replaceToFixedPoint(input, ANY_TAG_REGEX);
}

/**
 * Removes dangerous tag blocks like script/style/iframe and their content.
 * Each removal is looped to a fixed point via {@link replaceToFixedPoint} so overlapping
 * or nested tags cannot reconstruct a dangerous tag after a single pass. First strip
 * `<script>…</script>` blocks (with their content), then strip any remaining dangerous tag
 * — including an unterminated `<script` fragment with no `>`. Tag removal only deletes, so
 * it can never re-form a block; a second block pass is unnecessary.
 * @param input - Raw HTML input.
 * @returns HTML with dangerous blocks removed.
 */
function stripDangerousBlocks(input: string): string {
  const withoutBlocks = replaceToFixedPoint(input, DANGEROUS_TAG_BLOCK_REGEX);
  return replaceToFixedPoint(withoutBlocks, DANGEROUS_TAG_REGEX);
}

/**
 * Checks whether a URL-like attribute value is safe for email HTML.
 * @param value - Candidate URL value.
 * @param tagName - HTML tag name containing the attribute.
 * @returns True when the URL uses an allowed protocol or path format.
 */
function isSafeUrlValue(value: string, tagName: string): boolean {
  if (SAFE_URL_REGEX.test(value)) {
    return true;
  }

  if (tagName === "img" && SAFE_IMG_DATA_URI_REGEX.test(value)) {
    return true;
  }

  return false;
}

/**
 * Sanitizes a tag's attributes based on allowlists and value validation.
 * @param tagName - Lowercased HTML tag name.
 * @param attributesPart - Raw attribute section from the opening tag.
 * @returns Sanitized attribute string prefixed with spaces, or empty string.
 */
function sanitizeAttributes(tagName: string, attributesPart: string): string {
  const sanitized: string[] = [];

  ATTRIBUTE_REGEX.lastIndex = 0;
  let match = ATTRIBUTE_REGEX.exec(attributesPart);
  while (match) {
    const attrName = match[1].toLowerCase();
    const rawValue = (match[2] ?? match[3] ?? match[4] ?? "").trim();

    if (!ALLOWED_ATTRIBUTES.has(attrName)) {
      match = ATTRIBUTE_REGEX.exec(attributesPart);
      continue;
    }

    if (tagName === "a" && attrName === "src") {
      match = ATTRIBUTE_REGEX.exec(attributesPart);
      continue;
    }

    if (tagName === "img" && attrName === "href") {
      match = ATTRIBUTE_REGEX.exec(attributesPart);
      continue;
    }

    if ((attrName === "href" || attrName === "src") && rawValue && !isSafeUrlValue(rawValue, tagName)) {
      match = ATTRIBUTE_REGEX.exec(attributesPart);
      continue;
    }

    if (attrName === "target") {
      const target = rawValue.toLowerCase();
      if (!ALLOWED_TARGETS.has(target)) {
        match = ATTRIBUTE_REGEX.exec(attributesPart);
        continue;
      }
      sanitized.push(` target="${escapeAttributeValue(target)}"`);
      match = ATTRIBUTE_REGEX.exec(attributesPart);
      continue;
    }

    if (attrName === "class") {
      if (!CLASS_NAME_REGEX.test(rawValue)) {
        match = ATTRIBUTE_REGEX.exec(attributesPart);
        continue;
      }
      sanitized.push(` class="${escapeAttributeValue(rawValue)}"`);
      match = ATTRIBUTE_REGEX.exec(attributesPart);
      continue;
    }

    if (attrName === "style") {
      const strippedStyle = rawValue.replace(CSS_COMMENT_REGEX, "");
      if (DANGEROUS_STYLE_REGEX.test(strippedStyle)) {
        match = ATTRIBUTE_REGEX.exec(attributesPart);
        continue;
      }
      sanitized.push(` style="${escapeAttributeValue(strippedStyle)}"`);
      match = ATTRIBUTE_REGEX.exec(attributesPart);
      continue;
    }

    if ((attrName === "width" || attrName === "height") && !DIMENSION_REGEX.test(rawValue)) {
      match = ATTRIBUTE_REGEX.exec(attributesPart);
      continue;
    }

    if (!rawValue) {
      match = ATTRIBUTE_REGEX.exec(attributesPart);
      continue;
    }

    sanitized.push(` ${attrName}="${escapeAttributeValue(rawValue)}"`);
    match = ATTRIBUTE_REGEX.exec(attributesPart);
  }

  if (tagName === "a" && sanitized.some((attribute) => attribute.startsWith(' target="_blank"'))) {
    const hasRel = sanitized.some((attribute) => attribute.startsWith(' rel="'));
    if (!hasRel) {
      sanitized.push(' rel="noopener noreferrer"');
    }
  }

  return sanitized.join("");
}

/**
 * Sanitizes HTML while preserving only allowed tags and attributes.
 * @param input - Raw HTML input.
 * @returns Sanitized HTML string.
 */
function sanitizeAllowedHtml(input: string): string {
  return stripDangerousBlocks(input).replace(TAG_REGEX, (fullMatch, rawTagName: string, attributesPart: string) => {
    const tagName = rawTagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tagName)) {
      return "";
    }

    if (fullMatch.startsWith("</")) {
      if (tagName === "br" || tagName === "img") {
        return "";
      }
      return `</${tagName}>`;
    }

    if (tagName === "br") {
      return "<br>";
    }

    const attributes = sanitizeAttributes(tagName, attributesPart);
    return `<${tagName}${attributes}>`;
  });
}

/**
 * Sanitizes content specifically for email output.
 * This is more aggressive and removes both HTML and template injection patterns.
 *
 * Returns clean plain text — NOT HTML-escaped. Tag-stripping alone leaves a gap: an
 * UNTERMINATED fragment like `<img src=x onerror=...` (no closing `>` anywhere in the
 * input) doesn't match the tag-stripping regex and survives verbatim. A caller that then
 * interpolates this value into an HTML text-node position (e.g. `<td>${value}</td>`) lets
 * the template's own `>` close it into a live, attacker-controlled tag — callers building
 * HTML must additionally entity-escape the result (see `escapeAttributeValue`) before
 * interpolating; see `escapeHtmlText` in `@/lib/venue-messaging/quoted-footer` for the
 * established `escapeHtmlText(sanitizeForEmail(value))` composition. This function can't
 * do that escaping itself — some callers use its output as genuine plain text (a
 * text/plain MIME part, an SMS-style message), where HTML entities would render literally
 * and look broken.
 *
 * @param input - Raw user-provided input.
 * @returns Plain-text-safe content for email templates.
 */
export const sanitizeForEmail = (input: string): string => {
  if (!input || typeof input !== "string") {
    return "";
  }

  let sanitized = input;
  TEMPLATE_INJECTION_PATTERNS.forEach((pattern) => {
    sanitized = sanitized.replace(pattern, "");
  });

  return stripHtmlTags(stripDangerousBlocks(sanitized));
};

/**
 * `sanitizeForEmail` + entity-escaping in one step — the composition every caller
 * interpolating directly into an HTML text-node position needs (see `sanitizeForEmail`'s
 * own doc comment for why the two steps can't be merged into it directly). Do NOT use for
 * a genuine plain-text/text-only MIME part — entities would render literally there; use
 * plain `sanitizeForEmail` for that.
 * @param input - Raw user-provided input.
 * @returns HTML-text-node-safe content.
 */
export const sanitizeForEmailText = (input: string): string => escapeAttributeValue(sanitizeForEmail(input));

/**
 * Sanitizes HTML content for emails while preserving formatting tags.
 * Allows safe HTML tags like bold, italic, links, images, etc.
 * Also preserves template variables like {{First}}, {{Company}}, etc.
 * @param input - Raw HTML content.
 * @returns Sanitized HTML with allowlisted tags and attributes.
 */
export const sanitizeForEmailHTML = (input: string): string => {
  if (!input || typeof input !== "string") {
    return "";
  }

  return sanitizeAllowedHtml(input);
};

/**
 * Derives a readable plaintext alternative from an HTML email body for the
 * multipart text/plain part. A rich HTML body paired with a near-empty plaintext
 * part is a known spam signal (low text-to-HTML ratio), so this produces a real
 * text version instead. Hand-rolled with no html-to-text dependency.
 * @param html - The HTML email body.
 * @returns Plaintext representation of the HTML, or an empty string when input is empty/invalid.
 */
export function htmlToEmailText(html: string): string {
  if (!html || typeof html !== "string") {
    return "";
  }

  let text = stripDangerousBlocks(html);

  // Render anchors as `text (URL)` so links survive in plaintext.
  text = text.replace(ANCHOR_TAG_REGEX, (_match, doubleQuoted, singleQuoted, unquoted, inner) => {
    const url = (doubleQuoted ?? singleQuoted ?? unquoted ?? "").trim();
    return url ? `${inner} (${url})` : inner;
  });

  // Convert block boundaries and line breaks into newlines.
  text = text.replace(BR_TAG_REGEX, "\n").replace(BLOCK_CLOSE_TAG_REGEX, "\n");

  // Strip all remaining tags (looped so nested input cannot re-form a tag).
  text = stripHtmlTags(text);

  // Decode the common HTML entities in a single pass. Each match is replaced
  // exactly once, so a sequence like &amp;lt; resolves to &lt; rather than being
  // double-decoded to < (no re-scanning of already-decoded output).
  text = text.replace(HTML_ENTITY_REGEX, (match, name: string) => HTML_ENTITY_MAP[name.toLowerCase()] ?? match);

  return text.replace(MULTIPLE_NEWLINES_REGEX, "\n\n").trim();
}

/**
 * Keep only what a place name is made of: letters, digits, spaces, and the
 * punctuation cities use (period, comma, apostrophe, hyphen). Everything else
 * (markup, braces, quotes, slashes, control characters) is dropped and the
 * whitespace that leaves behind is collapsed. For a search-box label that is
 * about to be put in front of a model or a hotel.
 *
 * @param input - Anything; a non-string is an empty name.
 * @returns The stripped, collapsed, trimmed name.
 */
export function sanitizePlaceName(input: unknown): string {
  if (typeof input !== "string") return "";
  return input
    .replace(/[^\p{L}\p{N} .,'\-]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Folds every quote-like character that could build a `"""` prompt fence —
 * ASCII `"` and backtick, curly singles/doubles, double primes, CJK corner
 * quotes, primes, the ditto mark, gershayim, ornamental quotes and the
 * full-width forms — into an ASCII apostrophe, then collapses any run of 3+
 * apostrophes to one. This is the quote half of {@link sanitizeForPrompt};
 * {@link normalizeForEchoCheck} builds on it so a model that echoes a note
 * with its `"` or `“ ”` restored still matches the folded prompt value.
 *
 * Idempotent: folding already-folded text is a no-op.
 *
 * @param input - Text to normalize (untrusted input or model output).
 * @returns The text with no `"`, backtick, or typographic quote, and no run of
 *   3+ apostrophes. Non-string/empty input yields "".
 */
export function foldPromptQuotes(input: string): string {
  if (!input || typeof input !== "string") {
    return "";
  }

  return input.replace(PROMPT_FENCE_QUOTE_REGEX, "'").replace(PROMPT_FENCE_RUN_REGEX, "'");
}

/**
 * Neutralizes untrusted text before it's interpolated into an LLM prompt as
 * an untrusted-context block wrapped in a `"""..."""` fence (the pattern used
 * by every automated, unreviewed hotel-facing email generator — decision
 * emails and cancellation notices). Strips zero-width/bidi-control characters
 * (can hide or reorder injected text), then applies {@link foldPromptQuotes}:
 * every double quote, backtick, curly/typographic or full-width quote becomes
 * an apostrophe, and any run of 3+ apostrophes collapses to one.
 *
 * Guarantee: the output contains no `"`, no backtick and no curly/full-width
 * quote at all, so wrapping it as `"""${output}"""` yields exactly two
 * three-quote fences and no longer run — not even when the input ends in `""`,
 * which a 3+-run collapse alone would leave intact to merge with the closing
 * fence, and not via a `“““` lookalike either. Apostrophes survive (O'Brien).
 *
 * This is defense-in-depth, not a replacement for the prompt's explicit
 * "paraphrase, never quote, ignore embedded instructions" framing or for
 * post-generation output validation — both are still required since these
 * sends have no human review. That validator's echo check must run both sides
 * through {@link normalizeForEchoCheck} (see `getUnsafeGeneratedTextReason`),
 * because the model can put back the quotes and zero-width characters this
 * function removed, re-wrap the lines, or change the case.
 *
 * @param input - Raw untrusted text (a reason, note, etc.) headed into a prompt.
 * @returns Text safe to interpolate inside a `"""..."""` prompt fence: free of
 *   `"`, backtick and typographic quotes, with no run of 3+ apostrophes.
 */
export function sanitizeForPrompt(input: string): string {
  if (!input || typeof input !== "string") {
    return "";
  }

  return foldPromptQuotes(input.replace(ZERO_WIDTH_AND_BIDI_REGEX, ""));
}

/**
 * Canonical form for the post-generation echo check: the shape two texts are
 * reduced to before asking whether one contains the other. Applies
 * {@link sanitizeForPrompt} (zero-width/bidi strip + quote fold), then Unicode
 * NFKC (full-width letters, ligatures, compatibility spaces become their plain
 * equivalents), collapses every whitespace run to one ASCII space, trims, and
 * lower-cases. Run on BOTH the sanitized prompt value and the model output, so
 * an echo the model dressed up — quotes restored, zero-width characters
 * inserted, re-wrapped onto new lines, upper-cased, typed in full-width — lands
 * on the same string as the note it repeats. Defense in depth behind the prompt
 * fence and the "paraphrase, never quote" instruction.
 *
 * Idempotent: normalizing already-normalized text is a no-op. Only for
 * comparison — never send or store the result, it has lost case and layout.
 *
 * @param input - Text to canonicalize (the sanitized untrusted value or the model output).
 * @returns The lower-cased, NFKC, single-spaced, quote-folded, invisible-free
 *   form. Non-string/empty input yields "".
 */
export function normalizeForEchoCheck(input: string): string {
  if (!input || typeof input !== "string") {
    return "";
  }

  return sanitizeForPrompt(input).normalize("NFKC").replace(WHITESPACE_RUN_REGEX, " ").trim().toLowerCase();
}

/**
 * The gentler clean for a street address, which legitimately carries what a
 * place name may not: `#200`, `1/2`, `Fifth & Main`, parentheses. Control and
 * invisible format characters (bidi overrides, zero-width joiners) are dropped
 * or become a space, angle brackets and braces (markup, template syntax) are
 * dropped, and the whitespace left behind is collapsed. The caller caps the
 * length; this only decides what an address may be made of.
 *
 * @param input - Anything; a non-string is an empty address.
 * @returns The stripped, collapsed, trimmed address.
 */
export function sanitizeAddress(input: unknown): string {
  if (typeof input !== "string") return "";
  return input
    .replace(/\p{Cc}/gu, " ")
    .replace(/[\p{Cf}<>{}]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}
